import React, {
  useCallback, useEffect, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Snackbar, Fade } from '@material-ui/core';
import { CheckBox, GroupOutlined, PersonAddOutlined } from '@material-ui/icons';
import {
  compose, drop, filter,
  get, getOr, isEmpty,
  map, max, orderBy,
  replace, size,
} from 'lodash/fp';
import { FormattedMessage } from 'react-intl';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import XLSX from 'xlsx';
import {
  Button, Grid, Heading, Spinner, Subheading, TableFallbackMessage,
} from '../../common/components';
import * as contactsActions from '../../contacts/actions';
import {
  ContactAddDialog,
  ExcelDownloadBox,
  ExcelUploadBox,
  NewContactTable,
  UploadFailureDialog,
  UploadProgressDialog,
  UploadSuccessDialog,
} from '../components';
import * as actions from '../actions';
import messages from '../messages';

const Schema = Yup.object().shape({
  contacts: Yup.array().of(Yup.object().shape({
    id: Yup.number(),
    student: Yup.object().shape({
      grade: Yup.mixed()
        .test(
          'type',
          'Type must be one of `string` or `number`',
          value => typeof value === 'string' || typeof value === 'number',
        ),
      clazzName: Yup.mixed()
        .test(
          'type',
          'Type must be one of `string` or `number`',
          value => typeof value === 'string' || typeof value === 'number',
        ),
      // clazzNumber: Yup.mixed()
      //   .test(
      //     'type',
      //     'Type must be one of `string` or `number`',
      //     value => typeof value === 'string' || typeof value === 'number',
      //   ),
      name: Yup.string(),
      // .required(),
      phoneNumber: Yup.string(),
      email: Yup.string().email(),
    }),
    primaryGuardian: Yup.object().shape({
      name: Yup.string(),
      // .required(),
      relationship: Yup.string(),
      phoneNumber: Yup.string(),
      // .required(),
      email: Yup.string().email(),
    }),
  })).required(),
});

const S = {
  Button: styled(Button)`
    height: 40px;
    min-height: 40px;
    margin-left: 1rem;
    padding: 0 12px;
  `,
  PersonAddOutlined: styled(PersonAddOutlined)`
    margin-right: 8px;
    color: ${({ theme }) => theme.palette.grey[700]};
    font-size: 1.25rem;
  `,
  Divider: styled.div`
    height: 1px;
    margin-bottom: 24px;
    background-color: ${({ theme }) => theme.palette.grey[400]};
  `,
  SubheadingWrapper: styled.div`
    display: flex;
    align-items: flex-end;
  `,
  SmallSubheading: styled.h3`
    margin-right: 8px;
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    font-weight: 500;
    line-height: 1.53;
  `,
  ContactCounts: styled.p`
    color: ${({ theme }) => theme.palette.grey[700]};
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    line-height: 1.54;
  `,
  CheckBox: styled(CheckBox)`
    margin-right: 4px;
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: 1rem;
  `,
  Notice: styled.div`
    display: flex;
    align-items: center;
    /* XXX: 중복 연락처 체크 기능 임시 제거 */
    /* margin-right: auto; */
    padding: 0 8px;
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    font-weight: 500;
    line-height: 1.54;
  `,
  Option: styled.div`
    display: flex;
    align-items: center;
  `,
  GroupOutlined: styled(GroupOutlined)`
    margin-right: 8px;
    color: ${({ theme }) => theme.palette.grey[500]};
    font-size: 1.25rem;
  `,
  // GroupSelect: styled(GroupSelect)`
  //   margin-right: 16px;
  //   /* XXX: 중복 연락처 체크 기능 임시 제거 */
  //   margin-left: auto;
  // `,
  SelectedContactsCounter: styled.p`
    margin-left: auto;
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    text-align: right;
    line-height: 1.53;
  `,
  Data: styled.data`
    color: ${({ theme }) => theme.palette.status.info};
  `,
};

const NewContacts = ({ match }) => {
  const [file, setFile] = useState(null);
  // const [group, setGroup] = useState({ type: 'allGroup', value: 'allGroup' });
  const [open, setOpen] = useState(false);
  const [initialValues, setInitialValues] = useState(null);
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const groups = useSelector(state => state.contacts.groups);
  const count = useSelector(state => state.newContacts.count);
  const creationTask = useSelector(state => state.newContacts.creationTask);

  const dispatch = useDispatch();

  // const options = useMemo(() => {
  //   const gradeGroups = compose(
  //     groupBy(clazzGroup => clazzGroup.grade),
  //     get('clazzGroups'),
  //   )(groups.data);

  //   return [
  //     {
  //       id: 'allGroup', type: 'allGroup', value: 'allGroup', label: '입력한 그룹으로',
  //     },
  //     ...map.convert({ cap: false })((gradeGroup, key) => ({
  //       id: `gradeGroup_${key}`,
  //       type: 'gradeGroup',
  //       value: `gradeGroup_${key}`,
  //       label: (
  //         <S.Option>
  //           <S.GroupOutlined />
  //           <span>{`${key}학년 (${sumBy(g => g.contacts_count, gradeGroup)}명)`}</span>
  //         </S.Option>
  //       ),
  //       suboptions: [
  //         ...map(clazz => ({
  //           id: clazz.id, type: 'clazzGroup', value: clazz.id, label: `${clazz.class_name}반 (${clazz.contacts_count}명)`,
  //         }), gradeGroup),
  //       ],
  //     }), gradeGroups),
  //     ...compose(
  //       map(customGroup => ({
  //         id: `customGroup_${customGroup.id}`,
  //         type: 'customGroup',
  //         value: `customGroup_${customGroup.id}`,
  //         label: `${customGroup.name} (${customGroup.contacts_count}명)`,
  //       })),
  //       reject(customGroup => customGroup.is_unspecified_group),
  //       get('customGroups'),
  //     )(groups.data),
  //   ];
  // }, [groups.data]);

  // const labels = useMemo(() => [
  //   { id: 'allGroup', value: 'allGroup', label: '입력한 그룹으로' },
  //   ...map(clazzGroup => ({
  //     id: `clazzGroup_${clazzGroup.id}`,
  //     value: `clazzGroup_${clazzGroup.id}`,
  //     label: `${clazzGroup.grade}학년 ${clazzGroup.class_name}반`,
  //   }), get('clazzGroups', groups.data)),
  //   ...compose(
  //     map(customGroup => ({
  //       id: `customGroup_${customGroup.id}`,
  //       value: `customGroup_${customGroup.id}`,
  //       label: customGroup.name,
  //     })),
  //     reject(customGroup => customGroup.is_unspecified_group),
  //     get('customGroups'),
  //   )(groups.data),
  // ], [groups.data]);

  // const handleGroupChange = useCallback((event) => {
  //   if (event.currentTarget.dataset.type !== 'gradeGroup') {
  //     setGroup({
  //       type: event.currentTarget.dataset.type,
  //       value: event.target.value,
  //     });
  //   }
  // }, []);
  // const handleSubgroupChange = useCallback((event) => {
  //   event.stopPropagation();

  //   setOpen(null);

  //   setGroup({
  //     type: event.currentTarget.dataset.type,
  //     value: event.currentTarget.dataset.value,
  //   });
  // }, []);
  const handleOpen = useCallback(event => setOpen(event.currentTarget.dataset.name), []);
  const handleClose = useCallback((event) => {
    if (get('currentTarget.dataset.type', event) !== 'gradeGroup') {
      setOpen(null);
    }
  }, []);
  const handleSubmit = useCallback((values) => {
    setOpen('uploadProgress');

    dispatch(actions.createContactsRequest({
      sid: match.params.sid,
      contacts: filter(contact => contact.checked, values.contacts),
    }));
  }, [dispatch, match.params.sid]);
  const fetchGroups = useCallback((...args) => dispatch(contactsActions.fetchGroupsRequest(...args)), [dispatch]);
  const createContact = useCallback((...args) => dispatch(actions.createContactRequest(...args)), [dispatch]);
  const initCreationTask = useCallback((...args) => dispatch(actions.initCreationTask(...args)), [dispatch]);
  const initCount = useCallback((...args) => dispatch(actions.initCount(...args)), [dispatch]);

  useEffect(() => {
    fetchGroups({ sid: match.params.sid });
  }, [fetchGroups, match.params.sid]);

  useEffect(() => {
    if (file) {
      const reader = new FileReader();

      reader.onload = (event) => {
        const data = event ? event.target.result : reader.content;
        const workbook = XLSX.read(data, { type: 'binary' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = drop(2, XLSX.utils.sheet_to_json(worksheet, { header: 1 }));

        setInitialValues(compose(
          orderBy(['student.grade', 'student.clazzName', 'student.clazzNumber', 'student.name'], ['asc', 'asc', 'asc', 'asc']),
          map.convert({ cap: false })((row, index) => ({
            id: index,
            checked: !!(get('0', row) && get('1', row) && get('3', row) && get('6', row) && get('8', row)),
            student: {
              grade: getOr('', '0', row),
              clazzName: getOr('', '1', row),
              clazzNumber: getOr('', '2', row),
              name: getOr('', '3', row),
              phoneNumber: replace(/\-/g, '', getOr('', '4', row)),
              email: getOr('', '5', row),
            },
            primaryGuardian: {
              name: getOr('', '6', row),
              // TODO: 임시로 etc로 처리
              // relationship: getOr('', '7', row),
              relationship: {
                부: 'father',
                모: 'mother',
                조부: 'grand_father',
                조모: 'grand_mother',
                기타: 'etc',
              }[getOr('etc', '7', row)] || 'etc',
              phoneNumber: replace(/\-/g, '', getOr('', '8', row)),
              email: getOr('', '9', row),
            },
          })),
        )(rows));
      };

      reader.readAsBinaryString(file);
    } else {
      setInitialValues(null);
    }
  }, [file]);

  useEffect(() => {
    if (count.data.completed) {
      setOpen('uploadSuccess');
    } else if (count.data.completed === false) {
      setOpen('uploadFailure');
    }
  }, [count.data.completed, dispatch]);

  const handleErrorSnackbarClose = () => setOpenErrorSnackbar(false);

  const handleErrorSnackbarEnter = () => {
    setErrorMessage('저장에 실패하였습니다.');
  };

  const handleErrorSnackbarExited = () => {
    setErrorMessage(null);
  };

  useEffect(() => {
    if (creationTask.error) {
      setOpenErrorSnackbar(true);
      setOpen(null);

      dispatch(actions.initCreationTask());
    }
  }, [dispatch, creationTask.error]);

  return (
    <>
      <Grid container spacing={2} padding={{ top: 24, bottom: 72 }}>
        <Grid
          container
          item
          xs={12}
          margin={{ bottom: 24 }}
        >
          <Heading>
            <FormattedMessage {...messages.newContactsHeading} />
          </Heading>
        </Grid>
        <Grid
          container
          item
          justify="space-between"
          alignItems="center"
          xs={12}
          margin={{ bottom: 16 }}
        >
          <Subheading>
            <FormattedMessage {...messages.newContactsSubheading} />
          </Subheading>
          <S.Button
            type="button"
            variant="outlined"
            data-name="contactAdd"
            onClick={handleOpen}
          >
            <S.PersonAddOutlined />
            <FormattedMessage {...messages.newContactsContactAddButton} />
          </S.Button>
        </Grid>
        <Grid item xs={12}>
          <ExcelDownloadBox />
        </Grid>
        <Grid item xs={12}>
          <ExcelUploadBox file={file} setFile={setFile} />
        </Grid>
        <Grid item xs={12}>
          <S.Divider />
        </Grid>
        <Grid item xs={12}>
          <S.SubheadingWrapper>
            <S.SmallSubheading>
              <FormattedMessage
                {...messages.newContactsSmallSubheading}
                values={{ count: max([0, size(initialValues)]) }}
              />
            </S.SmallSubheading>
            {/* TODO: 중복 연락처 확인 기능 삭제로 임시 변경 */}
            {/* <S.ContactCounts>
              <FormattedMessage
                {...messages.newContactsContactCounts}
                values={{
                  new: 0,
                  duplicated: 0,
                }}
              />
            </S.ContactCounts> */}
          </S.SubheadingWrapper>
        </Grid>
        <Grid item xs={12}>
          <Formik
            initialValues={{ contacts: initialValues || [] }}
            validationSchema={Schema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ values, setFieldValue }) => (
              <Form>
                <Grid
                  container
                  item
                  alignItems="center"
                  xs={12}
                  margin={{ bottom: 16 }}
                >
                  {/* XXX: 중복 연락처 체크 기능 임시 제거 */}
                  {/* <S.Notice>
                    <S.CheckBox />
                    <FormattedMessage {...messages.newContactsNotice} />
                  </S.Notice> */}
                  {/* <S.GroupSelect
                    labels={labels}
                    options={options}
                    value={group.value}
                    name="groupSelect"
                    open={open === 'groupSelect'}
                    onOpen={handleOpen}
                    onClose={handleClose}
                    onChange={handleGroupChange}
                    onSubgroupChange={handleSubgroupChange}
                  /> */}
                  <S.SelectedContactsCounter>
                    선택한 연락처
                    {' '}
                    <S.Data value={size(filter(contact => contact.checked, values.contacts))}>{size(filter(contact => contact.checked, values.contacts))}</S.Data>
                    건
                  </S.SelectedContactsCounter>
                  <S.Button
                    type="submit"
                    // type={group.type === 'allGroup' ? 'submit' : 'button'}
                    color="primary"
                    variant="contained"
                    data-name="confirm"
                    disabled={isEmpty(filter(contact => contact.checked, values.contacts))}
                    // {...(group.type === 'allGroup' ? {} : { onClick: handleOpen })}
                  >
                    <FormattedMessage {...messages.newContactsButton} />
                  </S.Button>
                </Grid>
                <Grid item xs={12}>
                  <NewContactTable
                    contacts={isEmpty(values.contacts) ? null : initialValues}
                    values={values}
                    setFieldValue={setFieldValue}
                  />
                </Grid>
                {/* <ConfirmDialog
                  count={compose(
                    size,
                    filter(contact => contact.checked),
                    get('contacts'),
                  )(values)}
                  group={compose(g => (group.type === 'clazzGroup'
                    ? `${getOr('', 'grade', g)}학년 ${getOr('', 'class_name', g)}반`
                    : `${getOr('', 'name', g)}`),
                  find(g => toString(g.id) === last(split('_', group.value))),
                  get(group.type === 'clazzGroup' ? 'clazzGroups' : 'customGroups'))(groups.data)}
                  open={open === 'confirm'}
                  onClose={handleClose}
                /> */}
              </Form>
            )}
          </Formik>
        </Grid>
        {!file && (
          <Grid item xs={12}>
            <TableFallbackMessage>
              <FormattedMessage
                {...messages.newContactsTableFallbackMessage}
              />
            </TableFallbackMessage>
          </Grid>
        )}
      </Grid>
      <ContactAddDialog
        creationTask={creationTask}
        groups={groups.data}
        initCreationTask={initCreationTask}
        open={open === 'contactAdd'}
        onClose={handleClose}
        onSubmit={createContact}
      />
      <UploadProgressDialog
        count={count.data}
        open={open === 'uploadProgress'}
      />
      <UploadSuccessDialog
        count={count.data}
        initCount={initCount}
        open={open === 'uploadSuccess'}
        onClose={handleClose}
      />
      <UploadFailureDialog
        count={count.data}
        initCount={initCount}
        open={open === 'uploadFailure'}
        onClose={handleClose}
      />
      <Snackbar
        open={!!creationTask.data}
        TransitionComponent={Fade}
        ContentProps={{
          'aria-describedby': 'create-success-message',
        }}
        message={(
          <span id="create-success-message">
            새 연락처가 추가되었습니다.
          </span>
        )}
      />
      <Snackbar
        autoHideDuration={3500}
        open={openErrorSnackbar}
        onClose={handleErrorSnackbarClose}
        onEnter={handleErrorSnackbarEnter}
        onExited={handleErrorSnackbarExited}
        message={errorMessage}
        TransitionComponent={Fade}
      />
      {creationTask.loading && <Spinner />}
    </>
  );
};

NewContacts.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      sid: PropTypes.string,
    }).isRequired,
  }).isRequired,
};

export default NewContacts;
